<script>
  // @ts-check
  import { afterUpdate, onMount, onDestroy, tick } from "svelte";
  import Grid from "../../atoms/Grid.svelte";
  import { getBreakpoint } from "../../utilities/breakpoints";
  import { debounce } from "../../utilities/performance";

  let grid;
  let masonryElement;

  let screenWidth = 0;

  /** @type {string} */
  export let gridClass = "";

  /**
   * Number of divider columns per breakpoint
   * @type {{sm: 1 | 2 | 3, md: 1 | 2 | 3, lg: 1 | 2 | 3}} */
  export let numberOfColumnsPerBreakpoint = { sm: 1, md: 3, lg: 2 };

  /** @type {unknown[]} */
  export let items;

  /** 
  Total gridColumns for mobile, tablet and desktop
  @type {import('../../atoms/Grid.svelte').GRID_COLUMNS} */
  export let subGridColumns;

  // Code based on https://css-tricks.com/a-lightweight-masonry-solution/
  export const refreshLayout = async () => {
    if (window == undefined || masonryElement instanceof HTMLElement === false) {
      return;
    }
    //XL number of columns is the same as LG
    const currentNumberOfColumnsPerBreakpoint = {
      ...numberOfColumnsPerBreakpoint,
      xl: numberOfColumnsPerBreakpoint.lg
    };

    /* Here we get the breakpoint and the current weight of the breakpoint */
    /* the bigger the weight of the breakpoint the bigger the screen */
    const breakpoint = getBreakpoint(screenWidth);

    // Get the number of columns for the current breakpoint
    let currentNumberOfColumns = currentNumberOfColumnsPerBreakpoint[breakpoint] || 1;

    /* revert to initial positioning, no margin, note that the grid children can't have a
    margin top of it's own */
    grid.items.forEach((c) => c.style.removeProperty("margin-top"));

    /* if we have more than one column do some magic calculation */
    if (currentNumberOfColumns > 1) {
      /* The top element of each column does not need a margin offset
        so we remove the numberOfColumns first elements from the array */
      grid.items.slice(currentNumberOfColumns).forEach((currentItem, i) => {
        /* This one is smart, we are acessing the grid.items array and not the sliced version
        that way we get the element on the top of the current one */
        const previousItem = grid.items[i];
        /* Get the distance of the bottom of the previous element to the top of the page */
        const previousBottom = previousItem.getBoundingClientRect().bottom;

        /* Get the distance of the top of the current element to the top of the page */
        const currentTop = currentItem.getBoundingClientRect().top;
        /* Get the gap of the grid  with some checks */
        const gridGap = isNaN(grid.gap) ? 0 : grid.gap;

        /* Calculate the offset, the difference between the distances of the current and the element on top of it
        and adds the gridGap*/
        const currentOffset = previousBottom - currentTop + gridGap;

        /* Apply the margin top to the current element */
        currentItem.style.marginTop = `${currentOffset}px`;
      });
    }
  };

  const calcGrid = async (currentGrid) => {
    /* Just to make sure we are on the client side */
    if (window == undefined || currentGrid instanceof HTMLElement === false) {
      return;
    }

    await tick();
    if (currentGrid) {
      grid = {
        _el: currentGrid,
        gap: parseFloat(getComputedStyle(currentGrid).rowGap),
        /* Only get childNodes that are elements and have a grid-column-end property (in case of stretched elements) */
        items: [...currentGrid.childNodes].filter(
          (currentNode) =>
            currentNode.nodeType === 1 &&
            currentNode instanceof Element &&
            +getComputedStyle(currentNode).gridColumnEnd !== -1
        ),
        mod: 0
      };

      refreshLayout();
    }
  };

  let resizeObserver;
  onMount(() => {
    resizeObserver = new ResizeObserver(() => {
      if (masonryElement) {
        debouncedRefreshLayout();
      }
    });

    resizeObserver.observe(masonryElement);
  });

  afterUpdate(() => {
    debouncedRefreshLayout();
  });

  onDestroy(() => {
    if (resizeObserver && masonryElement) {
      resizeObserver.unobserve(masonryElement);
    }
  });

  $: if (masonryElement) {
    calcGrid(masonryElement);
  }

  $: if (items) {
    // eslint-disable-next-line no-self-assign
    masonryElement = masonryElement; // refresh masonryElement in case of items changing
  }

  const debouncedRefreshLayout = debounce(refreshLayout, 250);
</script>

<Grid bind:ref={masonryElement} gridClass="items-start {gridClass}" {subGridColumns}>
  <slot />
</Grid>

<svelte:window
  bind:innerWidth={screenWidth}
  on:resize={(event) => {
    const target = /** @type {Window} */ (event.target);

    if (target.innerWidth !== screenWidth) {
      debouncedRefreshLayout(event);
    }
  }}
/>
